// Submit (`/`, DESIGN_SPEC §11.2; IMPL_ADDENDUM §1, §2a, §2b, §2d; RESTYLE §7): Conifer's hero with the specimen plate as
// the drop target and the run sheet, then Recent runs across the flat band.
//   <SubmitPage />   keys: Ctrl+Enter submits · Esc clears the file or batch (or leaves URL mode) · Ctrl+V pastes files or a
//   URL anywhere
// Several files (picked, dropped or pasted) or one archive make a batch for POST /v1/jobs/bulk (features/submit/batch.ts:
// five files at most, archives unpacked on the server, one guest for all); a single plain file keeps the single path with
// its hash and portrait. Bulk is for signed-in viewers: a signed-out submit (or a 401) opens the sign-in dialog. One run
// opens its report; several open Analyses; a 207 keeps the batch and says which file stopped it.
// The guest profile is detected from the file (inferProfile, verbatim); the network is internet unless the viewer picks
// blocked (the simulated mode) for this submission, never remembered; the timeout is remembered per viewer. Every submission asks for its dropped files to be analyzed
// (auto_analyze_children); none asks for live input.
// Turnstile runs on its own (interaction-only: the widget shows only when Cloudflare wants a click). Every POST carries a
// fresh single-use token (header Cf-Turnstile-Token) and the widget is reset after every attempt; a submit made before the
// token arrives waits for it (up to TOKEN_WAIT_MS). A 403 shows inline and re-reads /v1/config; a failed check shows inline
// with "Retry the check". When /v1/config cannot be read the submit is left to the server. With sign-in required and no
// session, the gold CTA becomes a plain link "Sign in with GitHub to submit →" whose return path carries a typed URL
// (?url=, read back on load), and a 401 says the same.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "@/components/ui";
import { FleetReadout } from "@/components/shell/FleetReadout";
import { BatchLine, CtaBlock, Lockup, PickedLine, SeenLine, SubmitHero } from "@/features/submit/SubmitHero";
import { batchProfile, isArchiveName, makeBatch, needsBulk, type Batch } from "@/features/submit/batch";
import { SignInDialog } from "@/features/submit/SignInDialog";
import { DropOverlay, useWindowFileDrop } from "@/features/submit/DropOverlay";
import { RecentRuns } from "@/features/submit/RecentRuns";
import { RunSheet, TIMEOUTS, type NetworkChoice } from "@/features/submit/RunSheet";
import { ADVANCED_DEFAULTS, AdvancedDialog, type AdvancedValues } from "@/features/submit/AdvancedDialog";
import { SpecimenPortrait } from "@/features/submit/SpecimenPortrait";
import { pad4 } from "@/features/submit/portrait";
import { inferProfile, isWindowsProfile, routeProfile, type Mode } from "@/features/submit/inferProfile";
import { describeReadFailure, describeRejected, describeSubmitError, normalizeUrl, pastedUrl, type SubmitProblem } from "@/features/submit/submitError";
import { usePickFile } from "@/features/submit/usePickFile";
import { SEEN_LIMIT, useSeenBefore } from "@/features/submit/useSeenBefore";
import { useTurnstile } from "@/features/submit/useTurnstile";
import { keys, loginHref, useConfig, useHotkeys, useJobs, useMe, useNodes, useSeo, useTheme } from "@/hooks";
import { api, isApiError, type SubmitSpec } from "@/lib/api";

const TIMEOUT_KEY = "malbox-submit-timeout";
const DEFAULT_TIMEOUT = 120;
/** How long a submit waits for the Cloudflare token before saying the check did not answer. */
const TOKEN_WAIT_MS = 20_000;

function readTimeout(): number {
  try {
    const v = Number(localStorage.getItem(TIMEOUT_KEY));
    return (TIMEOUTS as readonly number[]).includes(v) ? v : DEFAULT_TIMEOUT;
  } catch {
    return DEFAULT_TIMEOUT;
  }
}

function storeTimeout(v: number) {
  try {
    localStorage.setItem(TIMEOUT_KEY, String(v));
  } catch {
    /* per-viewer convenience only */
  }
}

const isEditable = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement || (t instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "reset", "range", "file"].includes(t.type)));

type Target =
  | { kind: "file"; file: File }
  | { kind: "url"; url: string }
  | { kind: "batch"; files: File[] }
  | { kind: "archive"; file: File };

export function SubmitPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const configQ = useConfig();
  const config = configQ.data;
  const me = useMe({ enabled: !!config?.auth.enabled });
  const { theme } = useTheme();
  useSeo({
    title: "Submit a malware sample — malbox",
    description: "Submit a Windows PE, URL, or password-protected archive for free dynamic malware analysis. First report in about two minutes — decrypted HTTPS, YARA, Suricata, extracted C2 config, MITRE ATT&CK tags.",
    path: "/submit",
    keywords: "submit malware sample, analyze malware online, free malware analysis, upload malware sample, dynamic malware analysis, malware triage online",
  });
  const nodesQ = useNodes();
  const jobsQ = useJobs(SEEN_LIMIT);

  const [cols, setCols] = useState(240);
  const picker = usePickFile(cols);
  const pick = picker.state;
  const [mode, setMode] = useState<Mode>("file");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const [network, setNetwork] = useState<NetworkChoice>("internet");
  const [timeoutSecs, setTimeoutSecs] = useState(readTimeout);
  const [showSubmitter, setShowSubmitter] = useState(false);
  // User override for the Windows SKU. Null means "stick with whatever
  // inferProfile picked" (the default), so submits where the viewer
  // never touched the toggle still ship the inferred profile.
  const [windowsOverride, setWindowsOverride] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedValues>(ADVANCED_DEFAULTS);
  const [problem, setProblem] = useState<SubmitProblem | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  // When the batch is one archive, the viewer picks whether we expand
  // it server-side into one job per Windows runnable (default) or
  // analyze the archive itself as one payload (for samples that drop
  // the archive at runtime, e.g. an installer that unpacks itself).
  const [archiveMode, setArchiveMode] = useState<"expand" | "single">("expand");
  // Upload progress for the current submission. null = idle (no bar);
  // {phase:"upload", fraction, bytes, totalBytes} = PUT in flight; the
  // phase flips to "finalize" once bytes land and we're waiting on the
  // backend (sample finalize / archive extraction / bulk job inserts).
  const [progress, setProgress] = useState<null | { phase: "upload" | "finalize"; fraction: number; totalBytes?: number }>(null);
  /** The next picker selection adds to the batch instead of replacing it. */
  const adding = useRef(false);
  const [signInOpen, setSignInOpen] = useState(false);
  /** A submit made before the Cloudflare token arrived: it goes out as soon as the token does. */
  const [queued, setQueued] = useState<Target | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const turnstileOn = !!config?.turnstile.enabled;
  const ts = useTurnstile({ enabled: turnstileOn, sitekey: config?.turnstile.sitekey, theme });
  const checkFailed = turnstileOn && ts.status === "failed" ? (ts.error ?? "the check failed") : null;
  // a URL typed before signing in comes back with the sign-in round trip
  const back = mode === "url" && url.trim() ? `/?url=${encodeURIComponent(url.trim())}` : "/";
  const signIn = config?.auth.required && me.data && !me.data.authenticated ? loginHref(config, back) : null;
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const u = params.get("url");
    if (!u) return;
    setMode("url");
    setUrl(u);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const file = pick.status === "idle" ? null : pick.file;
  const inferred =
    mode === "file" && batch ? { profile: batchProfile(batch.files), reason: `batch · ${batch.files.length} ${batch.files.length === 1 ? "file" : "files"}` } : inferProfile(mode === "file" ? file : null, mode);
  const nodes = nodesQ.data;
  const available = useMemo(() => new Set((nodes ?? []).flatMap((n) => n.profiles)), [nodes]);
  // Apply the Windows SKU override BEFORE routing so routeProfile sees
  // the user's actual choice when checking fleet coverage.
  const inferredWithOverride =
    windowsOverride && isWindowsProfile(inferred.profile) && isWindowsProfile(windowsOverride)
      ? { profile: windowsOverride, reason: inferred.reason }
      : inferred;
  const route = routeProfile(inferredWithOverride, available);
  const seen = useSeenBefore(mode === "file" && pick.status === "picked" ? pick.analysis.sha256 : null);
  const specimenNo = jobsQ.data ? jobsQ.data.length + 1 : null;

  // a plain file keeps the single path (hash, portrait); several files or an archive make a batch
  const takeFiles = (files: File[]) => {
    if (!files.length) return;
    setMode("file");
    setProblem(null);
    const add = adding.current;
    adding.current = false;
    if (add && batch) return setBatch(makeBatch(files, batch));
    if (needsBulk(files)) {
      picker.clear();
      return setBatch(makeBatch(files));
    }
    setBatch(null);
    picker.pick(files[0]);
  };
  const takeRef = useRef(takeFiles);
  takeRef.current = takeFiles;
  const drop = useWindowFileDrop(useCallback((files: File[]) => takeRef.current(files), []));
  const dragging = drop.dragging;
  const signedIn = !!me.data?.authenticated;
  // bulk needs a session wherever the backend has sign-in at all
  const bulkNeedsSignIn = !!config?.auth.enabled && !signedIn;

  const submit = useMutation({
    mutationFn: async (t: Target) => {
      const spec: SubmitSpec = { profile: route.profile, network, timeout_secs: timeoutSecs };
      spec.auto_analyze_children = advanced.auto_analyze_children;
      spec.allow_input = advanced.allow_input;
      if (advanced.command.trim()) spec.command = advanced.command.trim();
      const labels = advanced.labels.split(",").map((l) => l.trim()).filter(Boolean);
      if (labels.length) spec.labels = labels;
      if (signedIn && showSubmitter) spec.show_submitter = true;
      // Per-submission progress hook: PUT bytes drive 0..1 under
      // phase:"upload", then the request to finalize/finalize-archive/
      // bulk-finalize flips to phase:"finalize" so the bar stops jumping
      // and the label changes.
      const totalBytes = t.kind === "file" || t.kind === "archive" ? t.file.size : t.kind === "batch" ? t.files.reduce((n, f) => n + f.size, 0) : undefined;
      setProgress({ phase: "upload", fraction: 0, totalBytes });
      const onProgress = (f: number) => setProgress({ phase: "upload", fraction: f, totalBytes });
      const opts = { turnstileToken: ts.token ?? undefined, onProgress };
      if (t.kind === "batch") {
        const res = { kind: "bulk" as const, runs: await api.submitBulk(t.files, spec, opts) };
        setProgress({ phase: "finalize", fraction: 1, totalBytes });
        return res;
      }
      if (t.kind === "archive") {
        const bytes = new Uint8Array(await t.file.arrayBuffer());
        const sha = await (await import("@/features/submit/sha256")).sha256Hex(bytes);
        const archive = await api.submitArchive(t.file, sha, spec, { ...opts, onProgress: (f) => { if (f >= 1) setProgress({ phase: "finalize", fraction: 1, totalBytes }); else onProgress(f); } });
        return { kind: "archive" as const, archive };
      }
      if (t.kind === "url") {
        setProgress({ phase: "finalize", fraction: 1 });
        return { kind: "one" as const, job: await api.submitURL({ ...spec, url: t.url }, { turnstileToken: ts.token ?? undefined }) };
      }
      const sha = pick.status === "picked" ? pick.analysis.sha256 : "";
      if (!sha) throw new Error("sha256 unavailable; file analysis incomplete");
      const job = await api.submitFilePresigned(t.file, sha, spec, { ...opts, onProgress: (f) => { if (f >= 1) setProgress({ phase: "finalize", fraction: 1, totalBytes }); else onProgress(f); } });
      return { kind: "one" as const, job };
    },
    onSettled: () => {
      ts.reset(); // tokens are single-use
      setProgress(null);
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: keys.allJobs });
      if (res.kind === "bulk") void qc.invalidateQueries({ queryKey: keys.bulkUsage });
      if (res.kind === "one") return void navigate(`/jobs/${res.job.id}`, { viewTransition: true });
      if (res.kind === "archive") {
        const jobs = res.archive.jobs ?? [];
        if (res.archive.dropped?.length && jobs.length === 0) {
          setProblem({ head: "Archive had nothing runnable", detail: res.archive.dropped.map((d) => `${d.name} — ${d.reason}`).join("\n"), action: "choose" });
          return;
        }
        setBatch(null);
        void navigate(jobs.length === 1 ? `/jobs/${jobs[0].id}` : "/jobs", { viewTransition: true });
        return;
      }
      const jobs = res.runs.jobs ?? [];
      if (res.runs.error) {
        // 207: the runs before the failing file were created; the batch stays so it can be fixed
        setProblem({
          head: jobs.length ? `${jobs.length} submitted, then one failed` : "Couldn't submit",
          detail: res.runs.error,
          action: jobs.length ? "analyses" : "choose",
        });
        return;
      }
      setBatch(null);
      void navigate(jobs.length === 1 ? `/jobs/${jobs[0].id}` : "/jobs", { viewTransition: true });
    },
    onError: (err, t) => {
      // bulk is for signed-in viewers: a 401 there asks to sign in rather than reporting a failure
      if (t.kind === "batch" && isApiError(err) && err.status === 401) {
        setSignInOpen(true);
        void qc.invalidateQueries({ queryKey: keys.me });
        return;
      }
      setProblem(describeSubmitError(err, t.kind === "archive" ? "batch" : t.kind));
      // a 401 means the session is gone: re-ask who is signed in, so the CTA turns into the sign-in link
      if (isApiError(err) && err.status === 401) void qc.invalidateQueries({ queryKey: keys.me });
      // a rejected check: the controller may have turned Turnstile on since /v1/config was read (or it never was)
      if (isApiError(err) && err.status === 403) void qc.invalidateQueries({ queryKey: keys.config });
    },
  });

  // until GET /v1/config answers we don't know whether a Turnstile token is required; if it cannot be read, the server decides
  const waitingForCheck = (!config && !configQ.isError) || (turnstileOn && !ts.token) || configQ.isFetching;
  const blocked = (detail: string) => setProblem({ head: "Couldn't verify this browser", detail: `${detail}; nothing was sent`, action: "recheck" });
  const fileProblem = useMemo(
    () => (pick.status === "rejected" ? describeRejected(pick.reason, pick.file.size) : pick.status === "failed" ? describeReadFailure(pick.message) : null),
    [pick],
  );
  const shownProblem = mode === "file" ? (problem ?? fileProblem) : problem;

  useEffect(() => {
    if (shownProblem) alertRef.current?.focus();
  }, [shownProblem]);

  // a queued submit goes out once the token arrives, and gives up when the check fails or does not answer
  useEffect(() => {
    if (!queued) return;
    if (checkFailed) {
      setQueued(null);
      blocked(`the Cloudflare check failed: ${checkFailed}`);
      return;
    }
    if (!waitingForCheck) {
      setQueued(null);
      submit.mutate(queued);
      return;
    }
    const timer = window.setTimeout(() => {
      setQueued(null);
      blocked("the Cloudflare check did not answer");
    }, TOKEN_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [queued, waitingForCheck, checkFailed]); // only when the queue or the check changes

  // a new file, a batch change or a switch between file and URL drops a submit still waiting for its token
  useEffect(() => setQueued(null), [pick, mode, batch]);

  const send = (t: Target) => {
    setProblem(null);
    if (checkFailed) return blocked(`the Cloudflare check failed: ${checkFailed}`);
    if (waitingForCheck) return setQueued(t);
    submit.mutate(t);
  };

  const doSubmit = () => {
    if (submit.isPending || queued || signIn) return;
    if (mode === "url") {
      const u = normalizeUrl(url);
      if (!u) {
        setUrlError(url.trim() ? "Not a web address. Use http or https, e.g. https://example.test/landing" : "Paste or type the address to open.");
        urlRef.current?.focus();
        return;
      }
      setUrl(u);
      send({ kind: "url", url: u });
      return;
    }
    if (batch) {
      if (!batch.files.length) return;
      if (bulkNeedsSignIn) return setSignInOpen(true);
      // One-archive batches route by the viewer's chosen archiveMode:
      // "expand" calls finalize-archive (one job per PE inside), "single"
      // uploads the archive itself as one sample (installer-style).
      if (batch.files.length === 1 && isArchiveName(batch.files[0].name)) {
        if (archiveMode === "expand") send({ kind: "archive", file: batch.files[0] });
        else send({ kind: "file", file: batch.files[0] });
        return;
      }
      send({ kind: "batch", files: batch.files });
      return;
    }
    if (pick.status !== "picked") return;
    send({ kind: "file", file: pick.file });
  };

  const enterUrlMode = (value?: string) => {
    setMode("url");
    setProblem(null);
    setUrlError(null);
    if (value !== undefined) setUrl(value);
  };
  const leaveUrlMode = () => {
    setMode("file");
    setProblem(null);
    setUrlError(null);
  };
  const choose = () => {
    setProblem(null);
    adding.current = false;
    picker.open();
  };

  // Ctrl+V anywhere: a file becomes the sample, a link opens URL mode (fields keep their own paste)
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (isEditable(e.target)) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length) {
        e.preventDefault();
        takeRef.current(files);
        return;
      }
      const u = pastedUrl(e.clipboardData?.getData("text/plain") ?? "");
      if (u) {
        e.preventDefault();
        setMode("url");
        setProblem(null);
        setUrlError(null);
        setUrl(u);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  useHotkeys({
    "Ctrl+Enter": () => {
      if (signIn || submit.isPending || queued) return false;
      // Same gate as the button: no submit (click OR keyboard) fires until
      // Turnstile has handed us a token. Keeps the UI from queueing requests
      // that will just park on waitingForCheck.
      if (turnstileOn && !ts.token) return false;
      if (mode === "file" && !(batch ? batch.files.length : pick.status === "picked")) return false;
      doSubmit();
    },
    Escape: (e) => {
      if (isEditable(e.target) && e.target !== urlRef.current) return false;
      if (mode === "url") return leaveUrlMode();
      if (batch) {
        setProblem(null);
        return setBatch(null);
      }
      if (pick.status !== "idle") {
        setProblem(null);
        return picker.clear();
      }
      return false;
    },
  });

  const problemAction = (p: SubmitProblem) => {
    switch (p.action) {
      case "signin":
        return (
          <a className="lnk" href={loginHref(config, "/")}>
            Sign in with GitHub
          </a>
        );
      case "choose":
        return (
          <button type="button" className="lnk" onClick={choose}>
            Choose another file
          </button>
        );
      case "edit-url":
        return (
          <button type="button" className="lnk" onClick={() => (setProblem(null), urlRef.current?.focus())}>
            Edit the URL
          </button>
        );
      case "check": // the widget was reset after the attempt (and /v1/config re-read); the retry waits for the fresh token
      case "retry":
        return (
          <button type="button" className="lnk" onClick={() => (setProblem(null), doSubmit())}>
            Try again
          </button>
        );
      case "analyses":
        return (
          <Link className="lnk" to="/jobs" viewTransition>
            Open Analyses
          </Link>
        );
      case "recheck":
        return (
          <button type="button" className="lnk" onClick={() => (setProblem(null), ts.retry())}>
            Retry the check
          </button>
        );
      default:
        return null;
    }
  };

  const status = batch
    ? `${batch.files.length} ${batch.files.length === 1 ? "file" : "files"} ready to submit`
    : pick.status === "hashing"
      ? `Hashing ${pick.file.name}`
      : pick.status === "picked"
        ? `${pick.file.name} ready: ${pick.analysis.type}, SHA-256 ${pick.analysis.sha256.slice(0, 8)}`
        : "";

  return (
    <>
      <div className="page">
        <SubmitHero
          left={
            <>
              <Lockup />
              <div className="mt-3"><FleetReadout /></div>
              <CtaBlock
                mode={mode}
                pick={pick}
                batch={mode === "file" ? batch : null}
                submitting={submit.isPending}
                waitingForCheck={!!queued}
                blocked={turnstileOn && !ts.token}
                signIn={signIn}
                url={url}
                urlError={urlError}
                urlRef={urlRef}
                onUrl={(v) => {
                  setUrl(v);
                  setUrlError(null);
                }}
                onChoose={choose}
                onPasteUrl={() => enterUrlMode()}
                onCancelUrl={leaveUrlMode}
                onSubmit={doSubmit}
              />
              {/* Turnstile, interaction-only: this slot takes room only while Cloudflare asks for a click */}
              {turnstileOn && <div ref={ts.ref} className={ts.interactive ? "mt-4 w-[min(480px,100%)] max-[899px]:order-3" : "h-0 overflow-hidden"} />}
              {shownProblem && (
                <Alert ref={alertRef} head={shownProblem.head} actions={problemAction(shownProblem)} className="mt-4 w-[min(480px,100%)] text-left max-[899px]:order-3">
                  {shownProblem.detail}
                </Alert>
              )}
              {mode === "file" && batch ? (
                <>
                  <BatchLine
                    batch={batch}
                    onRemove={(f) => setBatch((b) => (b ? { ...b, files: b.files.filter((x) => x !== f) } : b))}
                    onAdd={() => {
                      adding.current = true;
                      picker.open();
                    }}
                    onClear={() => setBatch(null)}
                  />
                  {batch.files.length === 1 && isArchiveName(batch.files[0].name) && (
                    <div className="mt-3 inline-flex flex-wrap items-center gap-2 border border-rule-strong bg-paper px-3 py-2 text-ui">
                      <span className="text-ink-soft">Treat archive as:</span>
                      <label className="inline-flex items-center gap-1.5 text-ink">
                        <input type="radio" name="archive-mode" checked={archiveMode === "expand"} onChange={() => setArchiveMode("expand")} />
                        <span>expand on server <span className="faint">— one job per Windows runnable inside</span></span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 text-ink">
                        <input type="radio" name="archive-mode" checked={archiveMode === "single"} onChange={() => setArchiveMode("single")} />
                        <span>one sample <span className="faint">— analyze the archive itself</span></span>
                      </label>
                    </div>
                  )}
                </>
              ) : (
                <PickedLine mode={mode} pick={pick} signIn={!!signIn} onChange={choose} />
              )}
              {mode === "file" && !batch && <SeenLine seen={seen} />}
              {progress && (
                <div className="mt-3" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress.fraction * 100)}>
                  <div className="flex items-baseline justify-between gap-3 text-meta text-ink-soft">
                    <span>
                      {progress.phase === "upload" ? "Uploading" : "Processing"}
                      {progress.totalBytes != null && progress.phase === "upload" && (
                        <> · {Math.round(progress.fraction * progress.totalBytes / (1024 * 1024))} / {Math.round(progress.totalBytes / (1024 * 1024))} MiB</>
                      )}
                    </span>
                    <span className="tabular-nums">{Math.round(progress.fraction * 100)}%</span>
                  </div>
                  <div className="mt-1 h-1 w-full overflow-hidden bg-paper border border-rule-row">
                    <div
                      className="h-full bg-ink transition-[width] duration-150 ease-out"
                      style={{ width: `${Math.max(2, Math.min(100, Math.round(progress.fraction * 100)))}%` }}
                    />
                  </div>
                </div>
              )}
              <RunSheet
                guest={{ profile: route.profile, note: route.note }}
                onGuestProfile={(p) => setWindowsOverride(p)}
                network={network}
                onNetwork={setNetwork}
                timeout={timeoutSecs}
                onTimeout={(s) => {
                  setTimeoutSecs(s);
                  storeTimeout(s);
                }}
                signedIn={signedIn}
                showSubmitter={showSubmitter}
                onShowSubmitter={setShowSubmitter}
                allowInput={advanced.allow_input}
                onAllowInput={(b) => setAdvanced({ ...advanced, allow_input: b })}
                autoAnalyzeChildren={advanced.auto_analyze_children}
                onAutoAnalyzeChildren={(b) => setAdvanced({ ...advanced, auto_analyze_children: b })}
                onAdvanced={() => setAdvancedOpen(true)}
              />
              <AdvancedDialog
                open={advancedOpen}
                values={advanced}
                onChange={setAdvanced}
                onClose={() => setAdvancedOpen(false)}
              />
            </>
          }
          specimen={
            <SpecimenPortrait
              mode={mode}
              pick={pick}
              batch={batch}
              specimenNo={specimenNo}
              dragging={dragging}
              url={url}
              onActivate={choose}
              onCols={setCols}
            />
          }
        />
        <input
          ref={picker.inputRef}
          type="file"
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = ""; // the same files can be chosen again
            takeFiles(files);
          }}
        />
        <p className="sr-only" role="status">
          {status}
        </p>
      </div>
      <div className="flat">
        <div className="page">
          <div className="journal">
            <RecentRuns no={1} />
          </div>
        </div>
      </div>
      <DropOverlay active={dragging} count={drop.count} specimenNo={specimenNo !== null ? pad4(specimenNo) : null} />
      <SignInDialog open={signInOpen} href={loginHref(config, "/")} onClose={() => setSignInOpen(false)} />
    </>
  );
}
